import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { haptic } from '@dorixona/ui'

/** Сколько протянуть пальцем (после сопротивления), чтобы отпускание обновило экран. */
export const PULL_THRESHOLD = 64
/** Дальше индикатор не тянется — палец идёт, полоса стоит. */
const PULL_MAX = 96
/** Сопротивление: полоса едет вдвое медленнее пальца, как в системных списках. */
const RESISTANCE = 0.5
/** Высота полосы, пока идёт обновление. */
const HOLD = 48

export interface PullToRefreshProps {
  onRefresh: () => Promise<unknown>
}

/**
 * «Потяни, чтобы обновить» для экрана, который прокручивает окно. Работает,
 * только когда страница в самом верху: тянуть начали ниже — это обычная
 * прокрутка. Полоса над содержимым растёт вслед за пальцем; перешли порог —
 * лёгкая отдача, отпустили — крутится, пока `onRefresh` не закончит.
 *
 * Слушатели пассивные: прокрутку не перехватываем, поэтому в Telegram
 * (вертикальные свайпы там выключены) и в WebView оболочки жест не спорит с
 * системным.
 */
export function PullToRefresh({ onRefresh }: PullToRefreshProps) {
  const { t } = useTranslation('common')
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const latest = useRef({ onRefresh, refreshing, pull })
  useEffect(() => {
    latest.current = { onRefresh, refreshing, pull }
  })

  useEffect(() => {
    let startY: number | null = null
    let armed = false

    const onStart = (event: TouchEvent) => {
      const finger = event.touches.item(0)
      if (!finger || event.touches.length > 1) return
      if (latest.current.refreshing || window.scrollY > 0) return
      startY = finger.clientY
      armed = false
    }
    const onMove = (event: TouchEvent) => {
      const finger = event.touches.item(0)
      if (startY === null || !finger) return
      const dy = finger.clientY - startY
      if (dy <= 0 || window.scrollY > 0) {
        setPull(0)
        if (dy < 0) startY = null
        return
      }
      const next = Math.min(PULL_MAX, dy * RESISTANCE)
      if (!armed && next >= PULL_THRESHOLD) {
        armed = true
        haptic('select')
      } else if (armed && next < PULL_THRESHOLD) armed = false
      setPull(next)
    }
    const onEnd = () => {
      if (startY === null) return
      startY = null
      setPull(0)
      if (latest.current.pull < PULL_THRESHOLD) return
      setRefreshing(true)
      void latest.current.onRefresh().finally(() => {
        setRefreshing(false)
      })
    }

    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd)
    window.addEventListener('touchcancel', onEnd)
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  const height = refreshing ? HOLD : pull
  const progress = Math.min(1, pull / PULL_THRESHOLD)

  return (
    <div
      className="ptr"
      data-state={refreshing ? 'refreshing' : pull > 0 ? 'pulling' : 'idle'}
      style={{ height }}
      aria-hidden={!refreshing}
    >
      {refreshing ? (
        <span className="ptr__ring" role="status" aria-label={t('state.refreshing')} />
      ) : (
        <span
          className="ptr__ring"
          style={{ opacity: progress, transform: `rotate(${progress * 270}deg)` }}
        />
      )}
    </div>
  )
}
